// File actions shared by every screen that shows an uploaded file: the job screen's
// panel, the client card, the publication form's outlet cards.
//
// Every file belongs to one outlet — it is the screenshot of how the piece appeared
// in that paper. So a list of a publication's files is a flat list of outlets and
// their screenshots, and each row carries the same two actions everywhere.
//
// Files are never streamed through our server — we ask for a short-lived signed URL
// and let the browser fetch it from R2 directly.
import { useLookups } from '../context/LookupsContext';
import he from '../i18n/he';
import { publicationsApi } from '../api';
import { messageForError } from '../i18n/errors';
import IconButton from './IconButton';
import { EyeIcon, DownloadIcon, CloseIcon } from './icons';

// Both actions do the same thing: ask the server for a signed URL, then hand it
// straight to window.open. View requests inline disposition so the image renders in
// the new tab; download takes the default attachment disposition, which makes the
// browser save the file (and close the tab it opened to do it).
//
// This is deliberately the same shape Trip Tracker uses against the same R2 setup,
// where it has been working in production. Both of my earlier attempts here failed
// by inventing something different instead:
//
//   - a synthetic anchor, appended, clicked, and removed on the very next line. The
//     click dispatches synchronously but the navigation is queued as a separate
//     task, so removing the element first cancelled it — silently, with no error
//     and no download. That is what broke downloads.
//   - opening a BLANK tab first and assigning tab.location after the await. That
//     needs the window reference, which made it fatal to pass 'noopener' — per spec
//     window.open then returns null, so the app's own tab navigated to the file.
//
// Passing the URL directly needs no window reference and queues no navigation of
// our own, so neither failure mode can exist. 'noopener' is safe precisely because
// the return value is never used.
//
// `getUrl` is the ONE thing that varies between file sources: it turns
// (id, { inline }) into a signed URL. It defaults to publications, so every
// existing caller is untouched, and the business-documents screen — whose files
// live under the office prefix and answer on their own endpoint — reuses these
// exact actions by passing its own. The open/alert behaviour above is subtle
// enough that a second copy of it would be a liability.
const openSigned = async (file, inline, getUrl = publicationsApi.downloadUrl) => {
  try {
    const { url } = await getUrl(file.id, { inline });
    if (url) window.open(url, '_blank', 'noopener');
  } catch (e) {
    window.alert(messageForError(e));
  }
};

export const viewFile = (file, getUrl) => openSigned(file, true, getUrl);
export const downloadFile = (file, getUrl) => openSigned(file, false, getUrl);

// View and download on EVERY file, everywhere it is listed, now as icon buttons
// with hover tooltips. `viewable` is decided by the server from the file's detected
// type — the browser never guesses which formats it can render, and a file it cannot
// render still downloads. `onRemove` (the modal only) removes that one screenshot,
// so it uses a plain × — reserving the trash icon for deleting a whole publication.
// `onOpened` fires when the file is actually viewed or downloaded — not when the row is
// rendered. Added for the work files a content writer uploads (migration 064), which
// carry a `seen_at`: the manager's notification asks "has anybody looked at this", and
// opening it IS the looking. Every other caller omits it and behaves exactly as before.
//
// It is deliberately fired alongside the open rather than awaited before it: clearing a
// doorbell must never delay or block the download somebody asked for.
export const FileActions = ({ file, onRemove, getUrl, onOpened, children }) => (
  <span className="file-actions">
    {file.viewable && (
      <IconButton
        icon={<EyeIcon />}
        label={he.publications.view}
        onClick={() => { viewFile(file, getUrl); if (onOpened) onOpened(file); }}
      />
    )}
    <IconButton
      icon={<DownloadIcon />}
      label={he.publications.download}
      onClick={() => { downloadFile(file, getUrl); if (onOpened) onOpened(file); }}
    />
    {/* A caller with its own third action (the documents screen's remove, which
        opens a choice rather than deleting) appends it here, so the row still
        reads as one toolbar. */}
    {children}
    {onRemove && (
      <IconButton icon={<CloseIcon />} label={he.publications.removeFile} onClick={() => onRemove(file)} />
    )}
  </span>
);

// One flat row per file: which outlet it is from, the file itself, then its actions.
// The outlet leads because it is what identifies the screenshot — the filename is
// usually whatever the phone or the scanner called it.
//
// `trailing` holds publication-level actions (edit / delete) that belong to the row
// as a whole, not to any one file. They render once, below the files, divided off so
// they can't be mistaken for another screenshot's controls.
export default function FileList({ files, onRemove, emptyText, trailing }) {
  const lookups = useLookups();
  const hasFiles = files && files.length > 0;
  return (
    <div className="file-list">
      {hasFiles
        ? files.map((f) => (
          <div key={f.id} className="file-row">
            <span className="badge gray">{lookups.outletLabel(f.outlet_code)}</span>
            <span className="file-name" title={f.original_name}>{f.original_name}</span>
            <FileActions file={f} onRemove={onRemove} />
          </div>
        ))
        : <span className="muted">{emptyText || he.publications.noFiles}</span>}
      {trailing && <div className="pub-actions">{trailing}</div>}
    </div>
  );
}
